import {AfterViewInit, Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FieldType, MemberAddeditComponent, MemberAddeditDialogModel} from '../member-addedit/member-addedit.component';
import {MatTableDataSource} from '@angular/material/table';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UiAlertService} from '../../../services/ui-alert/ui-alert.service';
import {VendorGroupsQuestionsService} from '../../../services/vendor-groups-questions/vendor-groups-questions.service';
import {VendorGroupsPlansService} from '../../../services/vendor-groups-plans/vendor-groups-plans.service';
import {VendorGroupsProductsService} from '../../../services/vendor-groups-products/vendor-groups-products.service';
import {MatSort} from '@angular/material/sort';
import {fromEvent} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, tap} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';

@Component({
    selector: 'app-vendor-groups-questions',
    templateUrl: './vendor-groups-questions.component.html',
    styleUrls: ['./vendor-groups-questions.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VendorGroupsQuestionsComponent implements OnInit, AfterViewInit {

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('search') search: ElementRef | undefined;

  protected allColumnNames = ['createdAt', 'vendorGroupPlanIds', 'category', 'sequence', 'questionText', 'type', 'metadata', 'actionmenu'];
  protected items = new MatTableDataSource<any>();
  private plans: any;
  private groupProducts: any;

  private addEditFields = [
    {
      name: 'vendorGroupPlanIds',
      label: 'Applies To',
      fieldType: FieldType.F_DROPDOWN,
      multiple: true,
      values: [],
      valuesLoader: () => this.loadGroupPlanDropdownValues(),
      searchable: true,
      searchPlaceholder: 'Search applicable vendor plans...',
      required: true
    },
    {name: 'category', label: 'Category', fieldType: FieldType.F_STRING, required: true},
    {name: 'sequence', label: 'Sequence #', fieldType: FieldType.F_NUMBER, required: true},
    {name: 'questionText', label: 'Question Text', fieldType: FieldType.F_STRING, required: true},
    {name: 'type', label: 'Type', fieldType: FieldType.F_DROPDOWN, values: ['Yes/No:boolean', 'Text:string', 'Number:number'], required: true},
    {name: 'metadata', label: 'Metadata', fieldType: FieldType.F_JSON, required: false},
  ];

  constructor(
      private vendorGroupsPlansService: VendorGroupsPlansService,
      private dialog: MatDialog,
      private snackBar: MatSnackBar,
      private uiAlertService: UiAlertService,
      private vendorGroupsQuestionsService: VendorGroupsQuestionsService,
      private vendorGroupsProductsService: VendorGroupsProductsService,
      private activatedRoute: ActivatedRoute,
      private router: Router
  ) {

  }

  ngOnInit(): void {
    this.reloadItems();
  }

  ngAfterViewInit(): void {
    this.activatedRoute.queryParamMap.subscribe(params => {
      // @ts-ignore
      this.search.nativeElement.value = params.get('search') || '';
      this.reloadItems();
    });
    fromEvent(this.search?.nativeElement, 'keyup').pipe(
        filter(Boolean),
        debounceTime(300),
        distinctUntilChanged(),
        tap((): void => {
          this.reloadItems();
          this.router.navigate([] , {
            relativeTo: this.activatedRoute,
            queryParams: {search: this.search?.nativeElement.value ?? ''},
            queryParamsHandling: 'merge'
          });
        })).subscribe();
  }

  getGroupPlanNames(item: any): string {
    return this.getQuestionVendorGroupPlanIds(item)
        .map(planId => this.plans?.find((thisPlan: { id: any; }) => `${thisPlan.id}` === `${planId}`))
        .map(plan => this.getGroupPlanLabel(plan))
        .filter(Boolean)
        .join(', ');
  }

  private reloadItems(): void {
    this.vendorGroupsQuestionsService.getAllVendorGroupQuestions(10000, 0, this.search?.nativeElement.value ?? '').then(questions => {
      this.items.data = questions;
      this.items.sort = this.sort;
    });
    this.loadGroupPlanDropdownValues();
  }

  private async loadGroupPlanDropdownValues(): Promise<string[]> {
    const [plans, products] = await Promise.all([
      this.vendorGroupsPlansService.getAllVendorGroupPlans(),
      this.vendorGroupsProductsService.getAllVendorGroupProducts()
    ]);

    this.plans = plans || [];
    this.groupProducts = products || [];
    this.addEditFields[0].values = this.getGroupPlanDropdownValues(this.plans);

    return this.addEditFields[0].values;
  }

  private getGroupPlanDropdownValues(plans: any[]): string[] {
    return (plans || [])
        .map((plan: any) => {
          const label = this.getGroupPlanLabel(plan);
          return label && plan?.id ? `${label}:${plan.id}` : '';
        })
        .filter(Boolean)
        .sort();
  }

  private getGroupPlanLabel(plan: any): string {
    if (!plan) {
      return '';
    }

    const productNames = this.getGroupProductsForPlan(plan)
        .map((product: any) => product.vendorProductName)
        .filter(Boolean)
        .join(', ');
    const planName = this.getFirstAvailablePlanMetadataValue(plan, ['name', 'displayName', 'planName', 'shortName', 'label']);
    const tier = plan.metadata?.tier;
    const labelParts = [productNames, planName, tier]
        .map((value: any) => `${value || ''}`.trim())
        .filter(Boolean);

    return Array.from(new Set(labelParts)).join(' - ') || `${plan.id || ''}`;
  }

  private getFirstAvailablePlanMetadataValue(plan: any, fieldNames: string[]): string {
    const metadata = plan?.metadata || {};
    return fieldNames
        .map(fieldName => `${metadata[fieldName] || ''}`.trim())
        .find(Boolean) || '';
  }

  private getGroupProductsForPlan(plan: any): any[] {
    const productIds = this.getVendorGroupProductIds(plan);
    return this.groupProducts?.filter((product: any) => productIds.includes(`${product.id}`)) || [];
  }

  private getVendorGroupProductIds(plan: any): string[] {
    const values = Array.isArray(plan?.vendorGroupProductIds)
        ? [...plan.vendorGroupProductIds]
        : [plan?.vendorGroupProductIds];

    if (plan?.vendorGroupProductId) {
      values.push(plan.vendorGroupProductId);
    }

    return Array.from(new Set(values
        .reduce((ids: string[], value: any) => ids.concat(`${value || ''}`.split(/[,.]/)), [])
        .map((value: string) => value.trim())
        .filter(Boolean)));
  }

  addItem(): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(null, this.addEditFields, 'Group Question')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.vendorGroupsQuestionsService.createVendorGroupQuestion(this.getQuestionSavePayload(dialogResult)).then(() => {
          this.reloadItems();
          this.snackBar.open('New group question created');
        });
      }
    });
  }

  async editItem(item: any): Promise<void> {
    await this.loadGroupPlanDropdownValues();

    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(this.getQuestionEditData(item), this.addEditFields, 'Group Question')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.vendorGroupsQuestionsService.modifyVendorGroupQuestion(item.id, this.getQuestionSavePayload(dialogResult)).then(() => {
          this.reloadItems();
          this.snackBar.open('Group question updated');
        });
      }
    });
  }

  async duplicateItem(item: any): Promise<void> {
    await this.loadGroupPlanDropdownValues();

    this.vendorGroupsQuestionsService.createVendorGroupQuestion(this.getQuestionSavePayload(this.getQuestionEditData(item))).then(() => {
      this.reloadItems();
      this.snackBar.open('Duplicate group question created');
    });
  }

  removeItem(item: any): void {
    this.uiAlertService.presentAlertConfirm('Do you really want to remove this group question?').then(ok => {
      this.vendorGroupsQuestionsService.deleteVendorGroupQuestion(item.id).then(() => {
        this.reloadItems();
        this.snackBar.open('Group question removed');
      });
    });
  }

  truncateJSONString(str: string, maxLength: number): string {
    str = JSON.stringify(str);
    return str.length <= maxLength ? str : str.slice(0, maxLength - 3) + '...';
  }

  private getQuestionEditData(item: any): any {
    return {
      ...item,
      vendorGroupPlanIds: this.getQuestionVendorGroupPlanIds(item),
    };
  }

  private getQuestionSavePayload(item: any): any {
    const vendorGroupPlanIds = this.getQuestionVendorGroupPlanIds(item);
    const metadata = this.getQuestionSaveMetadata(item, vendorGroupPlanIds);
    const payload = {
      ...item,
      vendorGroupPlanIds,
      groupPlanId: vendorGroupPlanIds[0] || null,
    };

    if (metadata !== undefined) {
      payload.metadata = metadata;
    }

    delete payload.id;
    delete payload.createdAt;
    delete payload.modifiedAt;

    return payload;
  }

  private getQuestionVendorGroupPlanIds(item: any): string[] {
    const values: any[] = Array.isArray(item?.vendorGroupPlanIds)
        ? item.vendorGroupPlanIds
        : [item?.vendorGroupPlanIds];
    const vendorGroupPlanIds: string[] = Array.from(new Set<string>(values
        .reduce((ids: string[], value: any) => ids.concat(`${value || ''}`.split(/[,.]/)), [])
        .map((value: string) => value.trim())
        .filter(Boolean)));

    if (vendorGroupPlanIds.length) {
      return vendorGroupPlanIds;
    }

    const metadataVendorGroupPlanIds = this.getQuestionVendorGroupPlanIdsFromMetadata(item);
    if (metadataVendorGroupPlanIds.length) {
      return metadataVendorGroupPlanIds;
    }

    return `${item?.groupPlanId || ''}`.trim() ? [`${item.groupPlanId}`.trim()] : [];
  }

  private getQuestionVendorGroupPlanIdsFromMetadata(item: any): string[] {
    const selectedTiers = this.getQuestionMetadataTiers(item?.metadata);
    if (!selectedTiers.length || !this.plans?.length) {
      return [];
    }

    const anchorPlan = this.getPlanById(item?.groupPlanId);
    const anchorProductIds = anchorPlan ? this.getVendorGroupProductIds(anchorPlan) : [];

    return (this.plans || [])
        .filter((plan: any) => selectedTiers.includes(`${plan?.metadata?.tier || ''}`.trim()))
        .filter((plan: any) => !anchorProductIds.length || this.hasSharedVendorGroupProduct(plan, anchorProductIds))
        .map((plan: any) => `${plan.id || ''}`.trim())
        .filter(Boolean);
  }

  private getQuestionSaveMetadata(item: any, vendorGroupPlanIds: string[]): any {
    const selectedTiers = this.getQuestionVendorGroupPlanTiers(vendorGroupPlanIds);

    if (!selectedTiers.length) {
      return item?.metadata;
    }

    return {
      ...this.getPlainMetadataObject(item?.metadata),
      tiers: selectedTiers,
    };
  }

  private getQuestionVendorGroupPlanTiers(vendorGroupPlanIds: string[]): string[] {
    const selectedPlanIds = new Set(vendorGroupPlanIds.map(planId => `${planId}`));

    return Array.from(new Set<string>((this.plans || [])
        .filter((plan: any) => selectedPlanIds.has(`${plan?.id}`))
        .map((plan: any) => `${plan?.metadata?.tier || ''}`.trim())
        .filter(Boolean)));
  }

  private getQuestionMetadataTiers(metadata: any): string[] {
    const tiers = this.getPlainMetadataObject(metadata)?.tiers;
    const values = Array.isArray(tiers) ? tiers : [tiers];

    return Array.from(new Set(values
        .map((tier: any) => `${tier || ''}`.trim())
        .filter(Boolean)));
  }

  private getPlanById(planId: any): any {
    const normalizedPlanId = `${planId || ''}`.trim();
    return (this.plans || []).find((plan: any) => `${plan?.id || ''}`.trim() === normalizedPlanId);
  }

  private hasSharedVendorGroupProduct(plan: any, productIds: string[]): boolean {
    const planProductIds = this.getVendorGroupProductIds(plan);
    return planProductIds.some(planProductId => productIds.includes(planProductId));
  }

  private getPlainMetadataObject(metadata: any): any {
    if (!metadata) {
      return {};
    }

    if (typeof metadata === 'string') {
      try {
        const parsedMetadata = JSON.parse(metadata);
        return parsedMetadata && typeof parsedMetadata === 'object' && !Array.isArray(parsedMetadata)
            ? parsedMetadata
            : {};
      } catch {
        return {};
      }
    }

    return typeof metadata === 'object' && !Array.isArray(metadata)
        ? {...metadata}
        : {};
  }

}
